import React, { useMemo, useRef, useState } from "react";
import { Package, Search } from "lucide-react";

export const SALES_LAST_GROUP_KEY = "salesLastProductGroupName";

export type SalesProductRow = {
  productId: string;
  name: string;
  sku: string | null;
  qtySold: number;
  tradeInQty?: number;
  sellingPrice: number | null;
  productImageUrl?: string | null;
  productImageVersion?: string | null;
  value: number | null;
  allowTradeIn?: boolean;
  tradeInValue?: number | null;
};

export type SalesGroupRow = { groupName: string; products: SalesProductRow[] };

export default function SalesProductEntryList({
  groupNames,
  groups,
  activeGroupName,
  onActiveGroupNameChange,
  searchQuery,
  onSearchQueryChange,
  qtySoldByProductId,
  tradeInQtyByProductId,
  onQtySoldChange,
  onTradeInQtyChange,
  availableQtyByProductId = {},
  cachedImageSrcByProductId = {},
  brokenImageProductIds = {},
  onBrokenImage,
  canEdit,
  needsDateFirst = false,
  onNeedDateFirst,
  loadingGroups = false,
}: {
  groupNames: string[];
  groups: SalesGroupRow[];
  activeGroupName: string;
  onActiveGroupNameChange: (name: string) => void;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  qtySoldByProductId: Record<string, number>;
  tradeInQtyByProductId: Record<string, number>;
  onQtySoldChange: (productId: string, qty: number) => void;
  onTradeInQtyChange: (productId: string, qty: number) => void;
  availableQtyByProductId?: Record<string, number>;
  cachedImageSrcByProductId?: Record<string, string>;
  brokenImageProductIds?: Record<string, boolean>;
  onBrokenImage: (productId: string) => void;
  canEdit: boolean;
  needsDateFirst?: boolean;
  onNeedDateFirst?: () => void;
  loadingGroups?: boolean;
}) {
  const [focusedPackSoldProductId, setFocusedPackSoldProductId] = useState<string | null>(null);
  const [focusedTradeInQtyProductId, setFocusedTradeInQtyProductId] = useState<string | null>(null);
  const [stockWarning, setStockWarning] = useState<{
    productId: string;
    productName: string;
    availableQty: number;
    field: "qtySold" | "tradeInQty";
  } | null>(null);
  const qtySoldInputRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const tradeInQtyInputRefs = useRef<Map<string, HTMLInputElement>>(new Map());

  const tabs = groupNames.length ? groupNames : groups.map((g) => g.groupName);
  const activeGroup = useMemo(
    () => groups.find((g) => g.groupName === activeGroupName) ?? null,
    [groups, activeGroupName]
  );
  const filteredProducts = useMemo(() => {
    const list = activeGroup?.products ?? [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) => (p.name ?? "").toLowerCase().includes(q) || (p.sku ?? "").toLowerCase().includes(q));
  }, [activeGroup, searchQuery]);

  const totals = useMemo(() => {
    let totalQty = 0;
    let totalValue = 0;
    for (const p of filteredProducts) {
      const qty = qtySoldByProductId[p.productId] ?? p.qtySold ?? 0;
      totalQty += qty;
      if (typeof p.sellingPrice === "number") totalValue += qty * p.sellingPrice;
    }
    return { totalQty, totalValue };
  }, [filteredProducts, qtySoldByProductId]);

  return (
    <>
      <style>{`
        input.no-number-spinners {
          -webkit-appearance: none;
        }
      `}</style>

      <div className="px-0 pt-0 pb-1 bg-gray-50">
        <div
          className="bg-gray-100 rounded-lg p-[4px] overflow-x-auto
          [&::-webkit-scrollbar]:h-1
          [&::-webkit-scrollbar-track]:bg-transparent
          [&::-webkit-scrollbar-thumb]:rounded-full
          [&::-webkit-scrollbar-thumb]:bg-theme-1/45
          hover:[&::-webkit-scrollbar-thumb]:bg-theme-1/60"
        >
          <div className="flex w-max min-w-full gap-1">
            {tabs.map((groupName) => {
              const active = groupName === activeGroupName;
              return (
                <button
                  key={groupName}
                  type="button"
                  className={`shrink-0 py-2 px-3 rounded-md text-sm font-medium whitespace-nowrap ${
                    active ? "bg-white text-primary shadow-[0_2px_4px_rgba(0,0,0,0.08)]" : "text-gray-600"
                  }`}
                  onClick={() => onActiveGroupNameChange(groupName)}
                >
                  {groupName}
                </button>
              );
            })}
            {loadingGroups && tabs.length === 0 ? (
              <div className="py-2 px-3 text-xs text-gray-500 animate-pulse">Loading...</div>
            ) : null}
            {!loadingGroups && tabs.length === 0 ? (
              <div className="py-2 px-3 text-xs text-gray-500">No groups</div>
            ) : null}
          </div>
        </div>
      </div>

      {!activeGroupName ? null : (
        <div className="mb-3 mt-2 flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-slate-900 truncate">{activeGroupName}</div>
            <div className="text-xs text-slate-500 mt-0.5">{filteredProducts.length} item(s)</div>
          </div>
          <div className="w-[180px] max-w-[50%]">
            <div className="flex items-center bg-white rounded-lg border border-gray-200 h-9 px-2">
              <Search size={16} className="text-gray-400 mr-1.5 shrink-0" />
              <input
                type="text"
                className="flex-1 outline-none border-0 shadow-none rounded-none p-0 appearance-none text-sm text-slate-900 placeholder:text-gray-400 bg-transparent min-w-0"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => onSearchQueryChange(e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {filteredProducts.length ? (
        <div className="pb-4">
          {filteredProducts.map((p) => {
            const currentQty = qtySoldByProductId[p.productId] ?? p.qtySold ?? 0;
            const sellingPrice = p.sellingPrice ?? null;
            const value = typeof sellingPrice === "number" ? currentQty * sellingPrice : null;
            const imageSrc = cachedImageSrcByProductId[p.productId] || p.productImageUrl || null;
            const showImage = Boolean(imageSrc) && !brokenImageProductIds[p.productId];
            const availableQty = availableQtyByProductId[p.productId];
            const hasStockLimit = typeof availableQty === "number";
            const currentTradeInQty = tradeInQtyByProductId[p.productId] ?? 0;
            const combinedExceedsStock =
              canEdit && hasStockLimit && currentQty + currentTradeInQty > availableQty;
            const exceedsStock = combinedExceedsStock;
            const exceedsTradeInStock = combinedExceedsStock;

            return (
              <div
                key={p.productId}
                role={needsDateFirst ? "button" : undefined}
                tabIndex={needsDateFirst ? 0 : undefined}
                onClick={needsDateFirst ? onNeedDateFirst : undefined}
                onKeyDown={
                  needsDateFirst
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") onNeedDateFirst?.();
                      }
                    : undefined
                }
                className={`rounded-xl border border-primary/40 shadow-[0_2px_8px_rgba(0,0,0,0.08)] mb-2 overflow-hidden bg-white ${
                  needsDateFirst ? "cursor-pointer" : ""
                }`}
              >
                <div className="flex flex-row p-3 bg-white">
                  <div className="w-[50px] h-[50px] rounded-lg bg-primary/5 border border-primary/30 flex items-center justify-center shrink-0">
                    {showImage ? (
                      <img
                        src={imageSrc!}
                        alt={p.name}
                        loading="lazy"
                        className="w-full h-full rounded-lg object-contain bg-white"
                        onError={() => onBrokenImage(p.productId)}
                      />
                    ) : (
                      <Package size={20} className="text-gray-400" />
                    )}
                  </div>

                  <div className="flex-1 ml-3 mr-3 flex flex-col justify-between min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-900 leading-5 line-clamp-3">{p.name}</div>
                        {p.sku ? (
                          <div className="mt-1 text-xs text-slate-500">
                            SKU: <span className="text-xs font-medium text-slate-700">{p.sku}</span>
                          </div>
                        ) : null}
                        <div className="mt-2 text-[12px] font-semibold text-slate-900 tabular-nums">
                          {typeof sellingPrice === "number"
                            ? `RM ${sellingPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : "—"}
                        </div>
                      </div>

                      <div className="flex items-start gap-2 ml-auto">
                        <div className="flex flex-col items-center min-w-[60px] shrink-0 gap-1">
                          <div className="h-3 flex items-center whitespace-nowrap text-[11px] font-medium text-slate-600 leading-3">
                            Qty Sold
                          </div>
                          {canEdit ? (
                            <div
                              className={`h-10 w-[42px] rounded-lg border bg-white flex items-center justify-center px-0 ${
                                exceedsStock ? "border-red-400" : "border-primary/40"
                              }`}
                            >
                              <input
                                ref={(el) => {
                                  if (el) qtySoldInputRefs.current.set(p.productId, el);
                                  else qtySoldInputRefs.current.delete(p.productId);
                                }}
                                type="text"
                                inputMode="numeric"
                                className="w-full text-center text-sm text-slate-900 outline-none border-0 bg-transparent shadow-none rounded-none p-0 tabular-nums no-number-spinners appearance-none"
                                value={
                                  focusedPackSoldProductId === p.productId && currentQty === 0 ? "" : String(currentQty)
                                }
                                onChange={(e) => {
                                  const digitsOnly = e.target.value.replace(/[^\d]/g, "");
                                  const parsed = digitsOnly === "" ? 0 : Number(digitsOnly);
                                  const safe = Number.isFinite(parsed) ? Math.max(0, Math.floor(parsed)) : 0;
                                  if (typeof availableQty === "number" && safe + currentTradeInQty > availableQty) {
                                    setStockWarning({
                                      productId: p.productId,
                                      productName: p.name,
                                      availableQty: Math.max(0, availableQty),
                                      field: "qtySold",
                                    });
                                  }
                                  onQtySoldChange(p.productId, safe);
                                }}
                                onFocus={() => setFocusedPackSoldProductId(p.productId)}
                                onBlur={() => {
                                  setFocusedPackSoldProductId((id) => (id === p.productId ? null : id));
                                }}
                              />
                            </div>
                          ) : (
                            <div className="h-10 w-[42px] flex items-center justify-center text-sm font-semibold text-slate-900 tabular-nums">
                              {currentQty.toLocaleString()}
                            </div>
                          )}

                          {p.allowTradeIn ? (
                            <>
                              <div className="mt-1.5 h-3 flex items-center whitespace-nowrap text-[11px] font-medium text-slate-600 leading-3">
                                Trade-In Qty
                              </div>
                              {canEdit ? (
                                <div
                                  className={`h-10 w-[42px] rounded-lg border bg-white flex items-center justify-center px-0 ${
                                    exceedsTradeInStock ? "border-red-400" : "border-cyan-400/70"
                                  }`}
                                >
                                  <input
                                    ref={(el) => {
                                      if (el) tradeInQtyInputRefs.current.set(p.productId, el);
                                      else tradeInQtyInputRefs.current.delete(p.productId);
                                    }}
                                    type="text"
                                    inputMode="numeric"
                                    className="w-full text-center text-sm text-slate-900 outline-none border-0 bg-transparent shadow-none rounded-none p-0 tabular-nums no-number-spinners appearance-none"
                                    value={
                                      focusedTradeInQtyProductId === p.productId && currentTradeInQty === 0
                                        ? ""
                                        : String(currentTradeInQty)
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      const digitsOnly = e.target.value.replace(/[^\d]/g, "");
                                      const parsed = digitsOnly === "" ? 0 : Number(digitsOnly);
                                      const safe = Number.isFinite(parsed) ? Math.max(0, Math.floor(parsed)) : 0;
                                      if (typeof availableQty === "number" && currentQty + safe > availableQty) {
                                        setStockWarning({
                                          productId: p.productId,
                                          productName: p.name,
                                          availableQty: Math.max(0, availableQty),
                                          field: "tradeInQty",
                                        });
                                      }
                                      onTradeInQtyChange(p.productId, safe);
                                    }}
                                    onFocus={() => setFocusedTradeInQtyProductId(p.productId)}
                                    onBlur={() => {
                                      setFocusedTradeInQtyProductId((id) => (id === p.productId ? null : id));
                                    }}
                                  />
                                </div>
                              ) : (
                                <div className="h-10 w-[42px] flex items-center justify-center text-sm font-semibold text-slate-900 tabular-nums">
                                  {currentTradeInQty.toLocaleString()}
                                </div>
                              )}
                            </>
                          ) : null}
                        </div>

                        <div className="flex flex-col items-end text-right gap-1">
                          <div className="h-3 flex items-center whitespace-nowrap text-[11px] font-medium text-slate-600 leading-3">
                            Value (RM)
                          </div>
                          <div className="h-10 flex items-center justify-end text-sm font-semibold text-slate-900 tabular-nums">
                            {typeof value === "number"
                              ? value.toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })
                              : "—"}
                          </div>
                          {p.allowTradeIn ? (
                            <>
                              <div className="mt-1.5 h-3 flex items-center whitespace-nowrap text-[11px] font-medium text-slate-600 leading-3">
                                Value (RM)
                              </div>
                              <div className="h-10 flex items-center justify-end text-sm font-semibold text-slate-900 tabular-nums">
                                0.00
                              </div>
                            </>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          <div className="fixed bottom-[60px] left-0 right-0 mx-auto w-full max-w-md z-[60] mt-0 bg-green-100/90 backdrop-blur supports-[backdrop-filter]:bg-green-100/80 border-t border-green-300/70 shadow-[0_-2px_8px_rgba(0,0,0,0.08)]">
            <div className="px-3 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[11px] font-semibold text-slate-600 leading-3">Total Pack Sold</div>
                <div className="text-base font-semibold text-slate-900 tabular-nums leading-5">
                  {totals.totalQty.toLocaleString()}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[11px] font-semibold text-slate-600 leading-3">Total Value (RM)</div>
                <div className="text-base font-semibold text-slate-900 tabular-nums leading-5">
                  {`RM ${totals.totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center text-center py-10">
          <Package size={48} className="text-gray-400" />
          <div className="mt-2 text-sm font-medium text-slate-700">No products found</div>
          {searchQuery ? <div className="mt-1 text-xs text-slate-500">Try adjusting your search</div> : null}
        </div>
      )}

      {stockWarning ? (
        <div className="fixed inset-0 z-[130] bg-black/60 flex items-start justify-center p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <div className="text-base font-semibold text-slate-900">Insufficient stock</div>
              <div className="text-sm text-slate-600 mt-1">
                Available stock for <span className="font-semibold">{stockWarning.productName}</span> is{" "}
                <span className="font-semibold">{stockWarning.availableQty.toLocaleString()}</span>. Qty Sold +
                Trade-In Qty cannot exceed this amount.{" "}
                {stockWarning.field === "tradeInQty" ? "Trade-In Qty" : "Quantity Sold"} has been reset.
              </div>
            </div>
            <div className="p-4">
              <button
                type="button"
                className="w-full rounded-xl bg-primary text-white py-2.5 text-sm font-semibold"
                onClick={() => {
                  const productId = stockWarning.productId;
                  const field = stockWarning.field;
                  if (field === "tradeInQty") {
                    onTradeInQtyChange(productId, 0);
                    setFocusedTradeInQtyProductId(productId);
                  } else {
                    onQtySoldChange(productId, 0);
                    setFocusedPackSoldProductId(productId);
                  }
                  setStockWarning(null);
                  window.setTimeout(() => {
                    if (field === "tradeInQty") tradeInQtyInputRefs.current.get(productId)?.focus();
                    else qtySoldInputRefs.current.get(productId)?.focus();
                  }, 0);
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
